import { useMemo, useState } from 'react';
import { useDeskClaims, type DeskClaim } from '../lib/useManagerData';
import { usePhoneCrumb } from '../lib/usePhoneCrumb';
import {
  Card, Chip, DataTable, EmptyState, Pane, PaneHead, StatCard, type TableRow,
} from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import { MonthFilterRow } from '../components/MonthFilterRow';
import { Bar } from '../components/Charts';
import {
  dayLabel, monthLabel, monthName, monthRange, money, num, todayYmd,
  monthChip,
} from '../lib/scope';
import { useReportMonth } from '../lib/useReportMonth';
import { exportCsv } from '@/pages/roles/lib/exportCsv';
import { ManagerSurface } from '../components/ManagerSurface';
import { ManagerMenu } from '../components/ManagerMenu';

/** A claim's terms, in days, before it is chased. The board's own clock. */
const TERMS_DAYS = 30;

/**
 * Clinic manager · Insurance & receivables — `g_ins.dc.html`.
 *
 * TWO CLOCKS, AND THEY ARE NOT THE SAME MONEY — the board's accent card is the
 * whole model and it is preserved exactly: APPROVED AND UNPAID is money the
 * insurer has agreed and owes; FILED AND NOT YET DECIDED is money still out for
 * a decision. A claim moves from the second into the first, then into the bank.
 *
 * `/billing/claims` is the endpoint built for this: it filters insurance
 * invoices server-side and returns the payer, because payer and authorisation
 * live in the invoice's ENCRYPTED notes and cannot be grouped in the client.
 *
 * WHAT IS THIN, and honestly: the seed tenant's claims carry no payer, so the
 * insurer dimension collapses to one "Payer not set" row. That is the data,
 * not the screen — a clinic that records payers gets a row per insurer.
 */
export function ManagerInsurancePage() {
  const { month, setMonth, compare, setCompare } = useReportMonth();
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const range = useMemo(() => monthRange(month), [month]);
  const desk = useDeskClaims();

  usePageMeta({ title: 'Insurance & receivables' });

  /* THE CLAIMS ARE THE DESK'S RECORDS (reception_claims) — the same rows the
     accountant's Receivables and Claims tab read, so "approved and unpaid" is
     one figure on every screen. The older invoice-derived claim list guessed a
     claim's state from its bill's payment status and printed a different
     total for the same money.

     WHAT AN INSURER OWES is a BALANCE, not a month's total: a claim APPROVED
     and not yet paid, whenever it was filed. A draft was never sent and a
     filed one is still being decided, so neither is owed yet. */
  const allClaims = desk.rows;
  const owedOf = (c: DeskClaim) =>
    c.status === 'approved' ? Math.max(0, c.amount - (c.paidAmount ?? 0)) : 0;
  const sentClaims = allClaims.filter((c) => c.status !== 'draft' && c.status !== 'held');

  /* DEDUCTIONS AND REJECTIONS — from the desk's claim records, which carry
     what was claimed, what the insurer paid, and why a rejection came back.
     A month is the claims FILED in it. */
  const deskInMonth = desk.rows.filter((c) => {
    const d = (c.filedAt ?? '').slice(0, 10);
    return d >= range.start && d <= range.end;
  });
  const settledClaims = deskInMonth.filter((c) => c.status === 'paid' && c.paidAmount !== null);
  const claimedSettled = settledClaims.reduce((s2, c) => s2 + c.amount, 0);
  const deducted = settledClaims.reduce((s2, c) => s2 + Math.max(0, c.amount - (c.paidAmount ?? 0)), 0);
  const deductionRate = claimedSettled ? Math.round((deducted / claimedSettled) * 1000) / 10 : null;
  const rejected = deskInMonth.filter((c) => c.status === 'rejected');
  const byReason = [...rejected.reduce((m, c) => {
    const r = (c.reason ?? '').trim() || 'No reason given';
    m.set(r, (m.get(r) ?? 0) + 1);
    return m;
  }, new Map<string, number>()).entries()].sort((a, b) => b[1] - a[1]);

  const monthClaims = deskInMonth.filter((c) => c.status !== 'draft' && c.status !== 'held');
  const filed = monthClaims.length;
  const paid = monthClaims.filter((c) => c.status === 'paid').length;
  const approvedUnpaid = allClaims.reduce((s2, c) => s2 + owedOf(c), 0);
  /* FILED AND NOT YET DECIDED — the board's second clock: sent, no answer yet. */
  const undecidedClaims = useMemo(
    () => allClaims.filter((c) => c.status === 'filed'),
    [allClaims],
  );
  const undecided = undecidedClaims.reduce((s2, c) => s2 + c.amount, 0);

  interface Insurer {
    id: string;
    name: string;
    sub: string;
    /** Claims the insurer decided in the clinic's favour. */
    approved: number;
    /** Claims sent, whatever came back. */
    filed: number;
    unpaid: number;
    oldest: string | null;
    late: number;
  }

  const insurers: Insurer[] = useMemo(() => {
    const m = new Map<string, Insurer>();
    const row = (name: string): Insurer => {
      const r = m.get(name) ?? {
        id: name, name, sub: '', approved: 0, filed: 0, unpaid: 0, oldest: null, late: 0,
      };
      m.set(name, r);
      return r;
    };
    /* This month's activity: claims sent, and the ones the insurer said yes to. */
    for (const c of monthClaims) {
      const r = row((c.insurer || 'Payer not set').trim());
      r.filed += 1;
      if (c.status === 'approved' || c.status === 'paid') r.approved += 1;
    }
    /* What is owed right now, whatever month it was filed in. */
    for (const c of sentClaims) {
      const out = owedOf(c);
      if (out <= 0) continue;
      const r = row((c.insurer || 'Payer not set').trim());
      r.unpaid += out;
      const at = (c.filedAt ?? '').slice(0, 10);
      if (at) {
        if (!r.oldest || at < r.oldest) r.oldest = at;
        if (daysSince(at) > TERMS_DAYS) r.late += 1;
      }
    }
    return [...m.values()]
      .map((r) => ({
        ...r,
        sub: r.unpaid === 0
          ? 'settled in full'
          : r.late > 0 ? 'the insurer with the oldest money' : 'within terms, and not yet paid',
      }))
      .sort((a, b) => b.unpaid - a.unpaid);
  }, [monthClaims, sentClaims]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return insurers;
    return insurers.filter((i) => i.name.toLowerCase().includes(q));
  }, [insurers, query]);

  const selected = selectedId ? insurers.find((i) => i.id === selectedId) ?? null : null;

  /* HOW LONG THE UNDECIDED MONEY HAS BEEN OUT — clock #2, which is the clock
     the accent card directly above this one explains. It used to age clock #1
     (approved and unpaid) under the other clock's caption, so the card and its
     own neighbour were about two different sums. */
  const ageing = useMemo(() => {
    const buckets = { fresh: 0, mid: 0, old: 0 };
    for (const c of undecidedClaims) {
      const out = c.amount;
      if (out <= 0) continue;
      const d = daysSince((c.filedAt ?? '').slice(0, 10) || todayYmd());
      if (d <= 30) buckets.fresh += out;
      else if (d <= 60) buckets.mid += out;
      else buckets.old += out;
    }
    return buckets;
  }, [undecidedClaims]);
  const ageingMax = Math.max(ageing.fresh, ageing.mid, ageing.old, 1);

  const rows: TableRow[] = shown.map((i) => ({
    id: i.id,
    cells: {
      insurer: (
        <div>
          <p className="t-strong">{i.name}</p>
          <p className="t-cap mut">{i.sub}</p>
        </div>
      ),
      approved: (
        <span className="num">
          {num(i.approved)}<span className="mut t-cap"> of {num(i.filed)}</span>
        </span>
      ),
      unpaid: <span className="w6 num">{num(i.unpaid)}</span>,
      clock: i.unpaid === 0
        ? <Chip kind="success" small>settled</Chip>
        : i.late > 0
          ? <Chip kind="danger" small>{i.late} past terms</Chip>
          : <Chip kind="success" small>within terms</Chip>,
    },
  }));

  if (rows.length) {
    rows.push({
      id: '__total',
      inert: true,
      cells: {
        insurer: <span className="t-sec w6">Approved and unpaid — total</span>,
        approved: <span />,
        unpaid: <span className="t-strong num">{num(approvedUnpaid)}</span>,
        clock: <span />,
      },
    });
  }

  const crumb = usePhoneCrumb('Reports', `Insurance · ${monthChip(month).split(' ')[0]}`);
  useShellChrome(
    {
      raw: true,
      ...crumb,
      /* Phone: the pick is its own screen; back clears it. */
      paneOpen: !!selected,
      onPaneClose: () => setSelectedId(null),
      phoneMenu: (close) => <ManagerMenu door="reports" onClose={close} />,
      title: 'Insurance & receivables',
      sub: `${monthLabel(month)} · all branches · vs ${monthName(compare)}`,
      search: 'Search an insurer, a claim, a rejection…',
      searchValue: query,
      onSearchChange: setQuery,
      primary: {
        label: 'Export',
        onClick: () =>
          exportCsv(`insurance-${month}`, ['Insurer', 'Claims', 'Unpaid', 'Past terms'],
            insurers.map((i) => [i.name, `${i.approved} of ${i.filed}`, Math.round(i.unpaid), i.late])),
      },
      pane: selected ? (
        <Pane
          head={<PaneHead overline="The selected insurer" title={selected.name} sub={selected.sub} />}
          foot={
            <button type="button" className="btn btn-primary" style={{ flex: 1 }} disabled
              title="Sending a statement needs a claims-statement endpoint, which does not exist">
              Send statement
            </button>
          }
        >
          <Card>
            <p className="t-ovl mut">Approved, unpaid</p>
            <p className="t-sec sec" style={{ marginTop: 4 }}>
              {sentClaims.filter((c) => (c.insurer || 'Payer not set') === selected.name
                && owedOf(c) > 0).length} claims · {money(selected.unpaid)}
            </p>
            <p className="t-cap mut">
              {selected.oldest
                ? `oldest dated ${dayLabel(selected.oldest)} · terms ${TERMS_DAYS} d`
                : `nothing outstanding · terms ${TERMS_DAYS} d`}
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Past terms</p>
            <p className={`t-sec ${selected.late ? 'red' : 'sec'}`} style={{ marginTop: 4 }}>
              {selected.late} claim{selected.late === 1 ? '' : 's'} older than {TERMS_DAYS} days
            </p>
          </Card>
          <Card>
            <p className="t-ovl mut">Deductions · rejections</p>
            {(() => {
              const mine = deskInMonth.filter((c) => (c.insurer || 'Payer not set') === selected.name);
              const cut = mine.filter((c) => c.status === 'paid' && c.paidAmount !== null)
                .reduce((s2, c) => s2 + Math.max(0, c.amount - (c.paidAmount ?? 0)), 0);
              const no = mine.filter((c) => c.status === 'rejected');
              return (
                <>
                  <p className={`t-sec ${cut || no.length ? 'amb' : 'sec'}`} style={{ marginTop: 4 }}>
                    {money(cut)} deducted · {no.length} rejected
                  </p>
                  {no.length ? (
                    <p className="t-cap mut">{[...new Set(no.map((c) => c.reason || 'no reason given'))].join(' · ')}</p>
                  ) : null}
                </>
              );
            })()}
          </Card>
          <Card kind="accent">
            <p className="t-sec brand">
              Only a recorded remittance clears the clock — not a promise, not a phone call.
            </p>
          </Card>
        </Pane>
      ) : undefined,
    },
    [month, compare, query, selectedId, approvedUnpaid, insurers.length, desk.rows.length],
  );

  return (
    <div className="maie desk">
      <ManagerSurface />
      <div className="dcontent">
        <MonthFilterRow
          month={month} onMonth={(m) => { setMonth(m); setSelectedId(null); }}
          compare={compare} onCompare={setCompare}
          branchLine="all branches"
        />

        <div className="kit-stats" style={{ display: 'flex', gap: 12, margin: '4px 0 20px' }}>
          <StatCard label="Filed" value={desk.isLoading ? '…' : num(filed)} />
          <StatCard label="Paid" value={desk.isLoading ? '…' : num(paid)} />
          <StatCard label="Deduction rate"
            value={deductionRate === null ? <span className="mut">nothing settled</span> : `${deductionRate}%`}
            tone={deductionRate ? 'warn' : undefined} />
          <StatCard label="Approved, unpaid" value={desk.isLoading ? '…' : num(approvedUnpaid)} tone={approvedUnpaid > 0 ? 'danger' : undefined} />
        </div>

        <DataTable
          caption="Insurers"
          columns={[
            { key: 'insurer', label: 'Insurer', width: 46 },
            { key: 'approved', label: 'Approved', width: 16 },
            { key: 'unpaid', label: 'Unpaid', width: 16 },
            { key: 'clock', label: 'Clock', width: 22 },
          ]}
          rows={rows}
          selectedId={selectedId}
          onSelect={(r) => {
            if (r.id === '__total') return;
            setSelectedId((cur) => (cur === r.id ? null : r.id));
          }}
          empty={
            <EmptyState
              title="No claim was filed this month"
              sub="A bill settled to an insurer becomes a claim here, with the clock its terms put on it."
            />
          }
        />

        <Card kind="accent">
          <p className="t-sec brand">
            Two clocks, and they are not the same money. <b>{num(approvedUnpaid)}</b> is approved
            and unpaid — the insurer has agreed it and owes it. <b>{num(undecided)}</b> is filed
            and not yet decided. A claim moves from the second into the first, then into the bank.
          </p>
        </Card>

        <div style={{ height: 8 }} />

        <div className="cm-split" style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">
                Filed, not yet decided — how long it has been out ({money(undecided)})
              </p>
              {undecided > 0 ? (
                <>
                  <Bar label="0–30 days" value={ageing.fresh} max={ageingMax} display={num(ageing.fresh)} tone="warn" />
                  <Bar label="31–60 days" value={ageing.mid} max={ageingMax} display={num(ageing.mid)} tone="warn" />
                  <Bar label="60+ days" value={ageing.old} max={ageingMax} display={num(ageing.old)} tone="danger" />
                </>
              ) : (
                <p className="t-sec mut">
                  Nothing is waiting on an insurer’s decision this month — every claim filed has
                  been answered.
                </p>
              )}
            </Card>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <Card>
              <p className="t-ovl mut mb8">Rejections · why, so the desk’s habit can be fixed</p>
              {byReason.length ? (
                byReason.map(([reason, n]) => (
                  <div key={reason} className="flex" style={{ justifyContent: 'space-between', marginTop: 4 }}>
                    <p className="t-sec sec">{reason}</p>
                    <p className="t-sec w6 num red">×{n}</p>
                  </div>
                ))
              ) : (
                <p className="t-sec mut">No claim filed this month came back rejected.</p>
              )}
            </Card>
          </div>
        </div>

        <p className="t-sec mut" style={{ marginTop: 12 }}>This is the number clinics lose silently.</p>
      </div>
    </div>
  );
}

function daysSince(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  const then = new Date(y, m - 1, d);
  const [ty, tm, td] = todayYmd().split('-').map(Number);
  return Math.round((new Date(ty, tm - 1, td).getTime() - then.getTime()) / 86400000);
}
